// ABOUTME: Web Worker-based SQL.js provider for better performance and Safari compatibility
// ABOUTME: Uses Web Worker to handle all database operations off the main thread

import { DataItem } from '../../types';
import { StorageProvider, StorageProviderConfig, QueryOptions } from './types';
import { WorkerClient } from './WorkerClient';
import { initTracker } from './initTracker';

export class SQLJSWorkerProvider implements StorageProvider {
    private workerClient: WorkerClient | null = null;
    private dbName: string;
    private storeName: string;
    private initialized: boolean = false;
    private initializationPromise: Promise<void> | null = null;

    constructor(config?: StorageProviderConfig) {
        this.dbName = config?.dbName || 'InfiniteListDB';
        this.storeName = config?.storeName || 'items';
    }

    async initialize(): Promise<void> {
        initTracker.addEvent('info', 'SQLJSWorkerProvider', 'initialize called', { 
            initialized: this.initialized, 
            hasPromise: !!this.initializationPromise 
        });
        
        if (this.initialized) {
            initTracker.addEvent('info', 'SQLJSWorkerProvider', 'Already initialized, returning');
            return;
        }

        // Prevent race conditions by reusing the same initialization promise
        if (this.initializationPromise) {
            initTracker.addEvent('info', 'SQLJSWorkerProvider', 'Reusing existing initialization promise');
            return this.initializationPromise;
        }

        initTracker.addEvent('info', 'SQLJSWorkerProvider', 'Starting new initialization');
        this.initializationPromise = this.doInitialize();
        return this.initializationPromise;
    }

    private async doInitialize(): Promise<void> {
        try {
            initTracker.addEvent('info', 'SQLJSWorkerProvider', 'doInitialize starting');
            
            // Create worker using Parcel's worker URL syntax
            let worker: Worker;
            try {
                initTracker.addEvent('info', 'SQLJSWorkerProvider', 'Creating worker with URL syntax');
                worker = new Worker(new URL('./sqljs-worker.ts', import.meta.url), {
                    type: 'module'
                });
                initTracker.addEvent('success', 'SQLJSWorkerProvider', 'Worker created successfully');
            } catch (workerError) {
                initTracker.addEvent('error', 'SQLJSWorkerProvider', 'Failed to create worker', {
                    error: workerError instanceof Error ? workerError.message : String(workerError)
                });
                throw new Error(`Unable to create Web Worker: ${workerError instanceof Error ? workerError.message : String(workerError)}`);
            }
            
            this.workerClient = new WorkerClient(worker);
            initTracker.addEvent('info', 'SQLJSWorkerProvider', 'Worker client created');
            
            // Initialize the database in the worker
            initTracker.addEvent('info', 'SQLJSWorkerProvider', 'Sending initialize message to worker');
            await this.workerClient.sendMessage('initialize');
            
            this.initialized = true;
            initTracker.addEvent('success', 'SQLJSWorkerProvider', 'Worker initialization completed');
        } catch (error) {
            initTracker.addEvent('error', 'SQLJSWorkerProvider', 'Initialization failed', {
                error: error instanceof Error ? error.message : String(error)
            });
            this.initializationPromise = null; // Reset so initialization can be retried
            
            // Cleanup worker if initialization failed
            if (this.workerClient) {
                this.workerClient.terminate();
                this.workerClient = null;
            }
            
            throw new Error(`Failed to initialize SQL.js Worker: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    async seedData(count: number, days?: number): Promise<void> {
        if (!this.workerClient) throw new Error('Worker not initialized');
        
        console.log(`Seeding ${count} items via worker...`);
        await this.workerClient.sendMessage('seedData', { count, days });
        console.log('Seeding completed');
    }

    async getItems(page: number, pageSize: number, options?: QueryOptions): Promise<DataItem[]> {
        if (!this.workerClient) throw new Error('Worker not initialized');

        return await this.workerClient.sendMessage<DataItem[]>('getItems', {
            page,
            pageSize,
            options
        });
    }

    async getTotalItemCount(options?: QueryOptions): Promise<number> {
        console.log('SQLJSWorkerProvider.getTotalItemCount() called', { 
            initialized: this.initialized, 
            hasWorker: !!this.workerClient
        });
        
        if (!this.workerClient) throw new Error('Worker not initialized');

        const count = await this.workerClient.sendMessage<number>('getTotalItemCount', { options });
        console.log('SQLJSWorkerProvider.getTotalItemCount() result:', count);
        return count;
    }

    async getCategories(): Promise<string[]> {
        if (!this.workerClient) throw new Error('Worker not initialized');

        return await this.workerClient.sendMessage<string[]>('getCategories');
    }

    async clearData(): Promise<void> {
        if (!this.workerClient) throw new Error('Worker not initialized');

        await this.workerClient.sendMessage('clearData');
    }

    async addItem(item: Omit<DataItem, 'id'>): Promise<DataItem> {
        if (!this.workerClient) throw new Error('Worker not initialized');

        return await this.workerClient.sendMessage<DataItem>('addItem', { item });
    }

    async updateItem(id: number, item: Omit<DataItem, 'id'>): Promise<DataItem> {
        if (!this.workerClient) throw new Error('Worker not initialized');

        return await this.workerClient.sendMessage<DataItem>('updateItem', { id, item });
    }

    async deleteItem(id: number): Promise<void> {
        if (!this.workerClient) throw new Error('Worker not initialized');

        return await this.workerClient.sendMessage<void>('deleteItem', { id });
    }

    getName(): string {
        return 'SQL.js (Worker)';
    }

    async getStorageInfo(): Promise<{ used: number; available?: number } | null> {
        if (!this.workerClient) {
            return null;
        }

        try {
            return await this.workerClient.sendMessage<{ used: number; available?: number } | null>('getStorageInfo');
        } catch (error) {
            console.error('Failed to get storage info from worker:', error);
            return null;
        }
    }

    /**
     * Export the raw SQLite database
     * @returns The database as a Uint8Array
     */
    async exportDatabase(): Promise<Uint8Array> {
        if (!this.workerClient) throw new Error('Worker not initialized');
        return await this.workerClient.sendMessage<Uint8Array>('exportDatabase');
    }

    // Cleanup method to terminate the worker
    terminate(): void {
        if (this.workerClient) {
            this.workerClient.terminate();
            this.workerClient = null;
        }
        this.initialized = false;
        this.initializationPromise = null;
    }
}